import { useCallback, useEffect, useRef, useState } from "react";

import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { useStore } from "../store";
import { useLiveKit } from "./LiveKitProvider";
import { useVoiceStore } from "./store";

const PTT_KEY = "v";
const SELF_MUTE_TOGGLE_KEY = "m";

/**
 * Floating mic HUD: a toggle button, a PTT settings popover, and a small
 * connection-state line. Hidden entirely when voicechat is disabled on
 * the server (no LIVEKIT_* env vars). Sticky toggle by default; settings
 * lets the user switch to push-to-talk (hold V).
 */
export function MicHud(): JSX.Element | null {
  const { room } = useLiveKit();
  const conn = useVoiceStore((s) => s.conn);
  const micEnabled = useVoiceStore((s) => s.micEnabled);
  const micPermission = useVoiceStore((s) => s.micPermission);
  const pttMode = useVoiceStore((s) => s.pttMode);
  const pttHeld = useVoiceStore((s) => s.pttHeld);
  const error = useVoiceStore((s) => s.error);
  const tokenInfo = useVoiceStore((s) => s.tokenInfo);

  const isTouch = useIsTouch();
  const isSpectator = useStore((s) => s.localPlayer?.isSpectator === true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const lastMicSetRef = useRef<boolean>(false);

  // Sticky mic state if not PTT, otherwise mic follows pttHeld.
  const effectiveMicOn = pttMode ? micEnabled && pttHeld : micEnabled;

  // Push the effective mic state to LiveKit whenever it changes.
  useEffect(() => {
    if (!room) return;
    if (effectiveMicOn === lastMicSetRef.current) return;
    lastMicSetRef.current = effectiveMicOn;
    void (async () => {
      try {
        await room.localParticipant.setMicrophoneEnabled(effectiveMicOn);
        if (effectiveMicOn) useVoiceStore.getState().setMicPermission(true);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        // eslint-disable-next-line no-console
        console.warn("[voice] setMicrophoneEnabled failed", err);
        // Permission denied = user intent can't be honored; reflect that.
        // Engine-not-connected = transient (LiveKit is reconnecting); keep
        // the user's sticky intent so the mic comes back on once the
        // connection recovers. lastMicSetRef stays at the requested value
        // so the next effect run doesn't no-op.
        if (/permission/i.test(msg)) {
          useVoiceStore.getState().setMicPermission(false);
          useVoiceStore.getState().setMicEnabled(false);
        } else if (/engine not connected|publishing rejected/i.test(msg)) {
          lastMicSetRef.current = !effectiveMicOn;
        } else {
          useVoiceStore.getState().setMicEnabled(false);
        }
      }
    })();
  }, [room, effectiveMicOn]);

  // Keyboard: M to toggle self mute (sticky mode), V hold for PTT.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (isTypingTarget(e.target)) return;
      if (e.repeat) return;
      const k = e.key.toLowerCase();
      if (pttMode && k === PTT_KEY) {
        e.preventDefault();
        useVoiceStore.getState().setPttHeld(true);
        // In PTT, "micEnabled" means "PTT system armed". We arm it on the
        // first press so users don't need to first click the mic button.
        if (!useVoiceStore.getState().micEnabled) {
          useVoiceStore.getState().setMicEnabled(true);
        }
        return;
      }
      if (!pttMode && k === SELF_MUTE_TOGGLE_KEY) {
        e.preventDefault();
        useVoiceStore
          .getState()
          .setMicEnabled(!useVoiceStore.getState().micEnabled);
      }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (pttMode && e.key.toLowerCase() === PTT_KEY) {
        useVoiceStore.getState().setPttHeld(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [pttMode]);

  const onMicClick = useCallback(() => {
    useVoiceStore.getState().setMicEnabled(!micEnabled);
  }, [micEnabled]);

  // Hide entirely if the server never sent a voice token (LiveKit disabled).
  if (!tokenInfo && conn !== "connecting" && conn !== "error") return null;

  // Spectators get a listen-only token from the server (canPublish: false).
  // Render a tiny indicator instead of the mic UI so the user knows their
  // mic is intentionally absent and isn't chasing a phantom failure.
  if (isSpectator) {
    return (
      <DraggableOverlay
        id="voice-mic-hud-spectator"
        defaultAnchor={{ right: 12, top: 60, safeAreaTop: isTouch }}
        enabled={isTouch}
        zIndex={20}
      >
        <div
          style={{
            padding: "4px 8px",
            background: "rgba(15,16,24,0.9)",
            border: "1px solid #2a2a36",
            borderRadius: 6,
            color: "#8aa0c8",
            fontSize: 11,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          Listen-only
        </div>
      </DraggableOverlay>
    );
  }

  const label = pttMode
    ? micEnabled
      ? pttHeld
        ? "Talking (V)"
        : "Hold V to talk"
      : "PTT armed off"
    : micEnabled
      ? "Mic on"
      : "Mic off";

  const showRed = !effectiveMicOn;

  return (
    <DraggableOverlay
      id="voice-mic-hud"
      // Top-right column, stacked below HudOverlay (App.tsx — wallet+nav row,
      // ~36px tall at top:12). Voice column below: SpeakerList at top:220,
      // PlayerVoicePanel at top:360 on touch.
      defaultAnchor={{ right: 12, top: 60, safeAreaTop: isTouch }}
      enabled={isTouch}
      zIndex={20}
      nonDragSelector="button, input, label"
    >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        alignItems: "flex-end",
        fontSize: 13,
        color: "#e8e8ef",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button
          onClick={onMicClick}
          title={pttMode ? "Arm push-to-talk" : "Toggle mic (M)"}
          style={{
            width: 44,
            height: 44,
            borderRadius: 22,
            border: showRed ? "2px solid #c84a4a" : "2px solid #4ac86a",
            background: showRed ? "#2a1414" : "#143a1e",
            color: showRed ? "#ffb0b0" : "#9bf0b8",
            fontSize: 18,
            cursor: "pointer",
          }}
        >
          {effectiveMicOn ? "🎤" : "🔇"}
        </button>
        <button
          onClick={() => setSettingsOpen((o) => !o)}
          title="Voice settings"
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            border: "1px solid #2a2a36",
            background: "rgba(15,16,24,0.92)",
            color: "#cfd2dd",
            cursor: "pointer",
          }}
        >
          ⚙
        </button>
      </div>
      <div
        style={{
          padding: "2px 6px",
          background: "rgba(15,16,24,0.78)",
          border: "1px solid #2a2a36",
          borderRadius: 4,
          fontSize: 11,
          color: showRed ? "#c8a0a0" : "#a8c8a0",
        }}
      >
        {label}
        {micPermission === false ? " · permission denied" : ""}
      </div>
      {conn === "error" && error ? (
        <div
          style={{
            padding: "2px 6px",
            background: "rgba(40,12,12,0.86)",
            border: "1px solid #c84a4a",
            borderRadius: 4,
            fontSize: 11,
            color: "#ffd0d0",
            maxWidth: 240,
          }}
        >
          Voice: {error}
        </div>
      ) : null}

      {settingsOpen && (
        <div
          style={{
            padding: 8,
            background: "rgba(15,16,24,0.94)",
            border: "1px solid #2a2a36",
            borderRadius: 6,
            minWidth: 200,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={pttMode}
              onChange={(e) => useVoiceStore.getState().setPttMode(e.target.checked)}
            />
            Push-to-talk (hold {PTT_KEY.toUpperCase()})
          </label>
          <div style={{ color: "#8a8e9e", fontSize: 11 }}>
            Default: sticky toggle. Use M to mute/unmute (sticky) or hold V (PTT).
          </div>
        </div>
      )}
    </div>
    </DraggableOverlay>
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}
